import { Callout, Colors, FormGroup } from '@blueprintjs/core'
import CX from 'classnames'
import React from 'react'
import RichEditor from '../../../components/editors/Rich'
import BarcodeInput from '../../../components/inputs/BarcodeInput'
import { InputLabel } from '../../../components/inputs/InputLabel'
import config from '../../../config'
import CHANNELS from '../../../constants/Channels'
import WeightInput from '../../../printing/components/WeightInput'
import { useScaleStreamer } from '../../../printing/scale'
import * as Inputs from '../../products/Inputs'
import {
  ItemDimensionInput,
  PaddingInput,
  ShippingWeightInput
} from '../../products_v5/components/JoinedForm/ProductForm/inputs'
import AddAdditionalDetailsDialog from '../../product_manager/components/ListingHistorySection/components/ListingActionDialog/AddAdditionalDetailsDialog'
import TagSelector from '../../product_manager/components/TagSelector'
import * as SgwInputs from '../listings/shopgoodwill/Inputs'
import { PRICE_OVERRIDES_DISABLED_PRICE_INPUT_TITLE_LABEL } from '../../products_v5/components/JoinedForm/utils'

const styles = {
  GRID_STYLE: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr' },
  callout: { marginBottom: 8, marginTop: 32 },
  hyperlinkColor: { color: Colors.BLUE2 }
}

const Title = ({ disabled, value, onChange, maxCharCount = 70 }) => {
  const invalid = value.length > maxCharCount
  return (
    <label className={CX('bp5-label')} style={{ maxWidth: 550 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Title
        <span className="bp5-text-muted" style={{ fontSize: '0.8em' }}>{`${value.length}/${maxCharCount}`}</span>
      </div>

      <input
        className={CX('bp5-input', { 'bp5-intent-danger': invalid })}
        type="text"
        disabled={disabled}
        value={value}
        placeholder="New Nike Black Shoes"
        onChange={e => onChange(e.target.value)}
        style={{ width: '100%' }}
      />
    </label>
  )
}

const BasicProductInfo = ({
  channel = null,
  listingId = null,
  carrierId,
  description,
  inventoryLocationId,
  weight,
  length,
  width,
  height,
  boxPadding,
  shippingSettings,
  onChange,
  shippingBoxId,
  shippingWeight,
  sku,
  supplierId,
  title,
  disablePickups,
  isDisabled,
  doOfferAdditionalInfo,
  variants,
  tags,
  overriddenValues,
  unitTracking,
  parseSkuQRCodePayloads,
  onSkuParseLoading
}) => {
  const [showingAdditionalInformation, setShowingAdditionalInformation] = React.useState(false)
  const [scaleName, scaleWeight, scaleConnected] = useScaleStreamer()

  const isSgw = channel?.name === CHANNELS.SHOPGOODWILL.name
  const isEbay = channel?.name === CHANNELS.EBAY.name
  const showAdditionalInfo = isSgw || isEbay
  const productHasVariants = variants?.length > 0

  const sgwMessage = `Since this item has bids, Shopgoodwill doesn't allow editing the description. But you can add additional information.`
  const ebayMessage =
    'Please note, if this item has bids you will need to add additional information instead. Bids are synced periodically and may not be up to date.'

  return (
    <div>
      <Title disabled={isDisabled} value={title} onChange={v => onChange('title', v)} maxCharCount={80} />
      {showAdditionalInfo && listingId && doOfferAdditionalInfo && (
        <Callout intent="primary" style={styles.callout}>
          {isSgw ? sgwMessage : ebayMessage}{' '}
          <a
            role="button"
            tabIndex={0}
            style={styles.hyperlinkColor}
            onClick={() => {
              setShowingAdditionalInformation(true)
            }}
          >
            Click here to add additional information.
          </a>
        </Callout>
      )}
      {showingAdditionalInformation && listingId && (
        <AddAdditionalDetailsDialog
          bidCount={null} // no need to warn them about possibly having zero bids, we already checked higher up the component tree
          listingId={listingId}
          onClose={changePerformed => {
            setShowingAdditionalInformation(false)

            if (changePerformed) {
              // AddAdditionalDetailsDialog will emit a success toast
              window.location.reload()
            }
          }}
          channel={channel}
        />
      )}
      <FormGroup label="Description">
        <RichEditor
          disabled={isDisabled}
          showTemplates
          value={description}
          onChange={v => onChange('description', v)}
        />
      </FormGroup>
      <div style={{ ...styles.GRID_STYLE, marginTop: 20 }}>
        <InputLabel title="SKU">
          <BarcodeInput
            style={{ width: 200 }}
            onChange={v => onChange('sku', v)}
            value={sku}
            supplierId={supplierId}
            placeholder="Enter an identifier..."
            parseQRCodePayloads={parseSkuQRCodePayloads}
            onParseLoading={onSkuParseLoading}
          />
        </InputLabel>

        {unitTracking ? (
          // Unit tracking products don't have a single supplier. It's stored on the units instead.
          // Future work may allow displaying a "unit editor" on these pages.
          <div /> // Don't disturb the grid layout
        ) : (
          <SgwInputs.Suppliers
            value={supplierId}
            onChange={v => onChange('supplierId', v)}
            autoload
            style={{ width: 200 }}
            title="Seller Store"
          />
        )}

        {unitTracking ? (
          <div /> // See Suppliers above
        ) : (
          <SgwInputs.InventoryLocation
            autoload
            onChange={v => onChange('inventoryLocationId', v)}
            // Not enough space to show (disabled for variants) in title without breaking into next line
            // use placeholder instead
            title="Inventory Location"
            placeholder={productHasVariants ? '(disabled for variants)' : 'Select'}
            disabled={productHasVariants}
            value={productHasVariants ? undefined : inventoryLocationId}
          />
        )}

        <div style={styles.GRID_STYLE}>
          <div className="products-v4-item-weight">
            <ItemDimensionInput
              title="Length"
              fieldName="length"
              value={length}
              onChange={e => onChange(e.name, e.value)}
              className="stretch-sixth"
            />
          </div>
        </div>

        <div style={styles.GRID_STYLE}>
          <div className="products-v4-item-weight">
            <ItemDimensionInput
              title="Width"
              fieldName="width"
              value={width}
              onChange={e => onChange(e.name, e.value)}
              className="stretch-sixth"
            />
          </div>
        </div>

        <div style={styles.GRID_STYLE}>
          <div className="products-v4-item-weight">
            <ItemDimensionInput
              title="Height"
              fieldName="height"
              value={height}
              onChange={e => onChange(e.name, e.value)}
              className="stretch-sixth"
            />
          </div>
        </div>

        {shippingSettings?.paddingChoices &&
          shippingSettings.paddingChoices.length > 0 &&
          (channel === CHANNELS.GOODWILLFINDS || channel === CHANNELS.SHOPGOODWILL || channel === CHANNELS.EBAY) && (
            <div style={{ ...styles.GRID_STYLE, gridTemplateColumns: `1fr 0.5fr` }}>
              <PaddingInput
                value={boxPadding}
                onChange={e => onChange(e.name, e.value)}
                paddingChoices={shippingSettings.paddingChoices}
                className="stretch-half nuke-top-margin-loadable-select"
              />
            </div>
          )}

        <Inputs.ShippingMethod
          value={carrierId}
          autoload
          process={d =>
            d.carriers
              .map(
                s =>
                  // screen dropdown from including Pickup option
                  !(disablePickups && s.id === 1) && { value: s.id, label: s.name }
              )
              .filter(c => c)
          }
          onChange={v => onChange('carrierId', v)}
        />

        <SgwInputs.ShippingBox
          title="Shipping Box"
          autoload
          value={shippingBoxId}
          onChange={v => onChange('shippingBoxId', v)}
        />
      </div>
      <div style={styles.GRID_STYLE}>
        <div className="products-v4-item-weight">
          <WeightInput
            style={{ height: 36 }}
            fill
            label="Item Weight"
            value={weight}
            onValueChange={(_, v) => onChange('weight', v)}
            minorStepSize={0.001}
            scaleWeight={scaleWeight}
            scaleName={scaleName}
            scaleConnected={scaleConnected}
          />
        </div>

        <ShippingWeightInput
          weight={weight}
          carrierId={carrierId}
          shippingBoxId={shippingBoxId}
          value={shippingWeight}
          onChange={v => onChange('shippingWeight', v.value)}
        />
      </div>
      {config.PRODUCT_TAGS() && (
        <div style={{ ...styles.GRID_STYLE, gridTemplateColumns: `1fr 0.5fr` }}>
          <FormGroup
            label="Tags"
            className="stretch-half nuke-top-margin-loadable-select"
            helperText={!!overriddenValues && PRICE_OVERRIDES_DISABLED_PRICE_INPUT_TITLE_LABEL}
          >
            <TagSelector value={tags} onChange={value => onChange('tags', value)} disabled={!!overriddenValues} />
          </FormGroup>
        </div>
      )}
    </div>
  )
}

export default React.memo(BasicProductInfo)
